import {
  CANONICAL_HOSTS,
  hostAvatarAsset,
  resolveCanonicalHost,
  type CanonicalHostId,
} from "@cue-the-night/host-engine";
import * as QRCode
  from "qrcode";

import type {
  HostedGameSnapshot,
  HostedPartySnapshot,
} from "@cue-the-night/transport";

import {
  resolveWebSocketUrl,
} from "./config";

import {
  HostedPartyClient,
  HostedPartyClientError,
} from "./hosted-party-client";

import {
  renderOwnerGame,
} from "./game-ui";

import {
  OWNER_GAME_CATEGORIES,
  ownerGamesForCategory,
  type OwnerCategoryId,
  type OwnerGameId,
} from "./owner-catalogue";

const OWNER_STORAGE_KEY =
  "ctn.owner.reconnect";

interface SavedOwnerSession {
  readonly joinCode:
    string;

  readonly reconnectToken:
    string;
}

function escapeHtml(
  value: string,
): string {
  const element =
    document.createElement(
      "div",
    );

  element.textContent =
    value;

  return element.innerHTML;
}

function readSavedOwnerSession():
SavedOwnerSession | undefined {
  const raw =
    localStorage.getItem(
      OWNER_STORAGE_KEY,
    );

  if (!raw) {
    return undefined;
  }

  try {
    const parsed =
      JSON.parse(raw) as
        Partial<
          SavedOwnerSession
        >;

    if (
      typeof parsed.joinCode !==
        "string" ||
      typeof parsed.reconnectToken !==
        "string"
    ) {
      return undefined;
    }

    return {
      joinCode:
        parsed.joinCode,

      reconnectToken:
        parsed.reconnectToken,
    };
  } catch {
    return undefined;
  }
}

function saveOwnerSession(
  session:
    SavedOwnerSession,
): void {
  localStorage.setItem(
    OWNER_STORAGE_KEY,
    JSON.stringify(
      session,
    ),
  );
}

function clearOwnerSession():
void {
  localStorage.removeItem(
    OWNER_STORAGE_KEY,
  );

  document
    .querySelector(
      ".owner-new-night-action",
    )
    ?.remove();
}

function consumeFreshOwnerRequest():
void {
  const url =
    new URL(
      window.location.href,
    );

  if (
    url.searchParams.get(
      "fresh",
    ) !== "1"
  ) {
    return;
  }

  clearOwnerSession();

  window.history.replaceState(
    null,
    "",
    "/owner",
  );
}

function installOwnerNewNightAction():
void {
  if (!readSavedOwnerSession()) {
    document
      .querySelector(
        ".owner-new-night-action",
      )
      ?.remove();

    return;
  }

  if (
    document.querySelector(
      ".owner-new-night-action",
    )
  ) {
    return;
  }

  const button =
    document.createElement(
      "button",
    );

  button.type =
    "button";

  button.className =
    "owner-new-night-action";

  button.textContent =
    "New Night";

  button.addEventListener(
    "click",
    () => {
      const saved =
        readSavedOwnerSession();

      if (
        saved &&
        !window.confirm(
          "Start a new Night? The current Owner session will no longer reopen automatically.",
        )
      ) {
        return;
      }

      clearOwnerSession();

      window.location.assign(
        "/owner?fresh=1",
      );
    },
  );

  document.body.append(
    button,
  );
}

function canonicalizeOwnerUrl(
  joinCode:
    string,
): void {
  window.history.replaceState(
    null,
    "",
    `/owner/${encodeURIComponent(joinCode)}`,
  );
}

function joinUrl(
  joinCode:
    string,
): string {
  return (
    window.location.origin +
    `/join/${encodeURIComponent(joinCode)}`
  );
}

export async function startOwnerApp(
  app:
    HTMLElement,
): Promise<void> {
  consumeFreshOwnerRequest();

  installOwnerNewNightAction();

  let client:
    HostedPartyClient | undefined;

  let unsubscribeParty:
    (() => void) | undefined;

  let unsubscribeGame:
    (() => void) | undefined;

  let snapshot:
    HostedPartySnapshot | undefined;

  let gameSnapshot:
    HostedGameSnapshot | undefined;

  let stagePairCode =
    "";

  let selectedCategory:
    OwnerCategoryId =
      "party";

  let selectedGame:
    OwnerGameId | undefined;

  async function chooseHostForNewParty():
  Promise<CanonicalHostId> {
    app.innerHTML = `
      <main class="owner-shell owner-host-choice-screen">
        <header class="owner-hero">
          <p class="eyebrow">
            CUE THE NIGHT
          </p>

          <h1>
            Choose<br />your host.
          </h1>

          <p class="subtitle">
            Same night. Very different energy.
          </p>
        </header>

        <section class="owner-host-choice-grid">
          ${CANONICAL_HOSTS
            .map(
              (host) => `
                <button
                  class="owner-host-choice-card"
                  type="button"
                  data-host-id="${host.id}"
                >
                  <span class="owner-host-choice-art">
                    <img
                      src="${hostAvatarAsset(host.id)}"
                      alt="${escapeHtml(host.displayName)}"
                    />
                  </span>

                  <span class="owner-host-choice-copy">
                    <span class="meta">
                      YOUR HOST
                    </span>

                    <strong>
                      ${escapeHtml(host.displayName)}
                    </strong>

                    <b>
                      ${escapeHtml(host.title)}
                    </b>

                    <span>
                      ${escapeHtml(host.tagline)}
                    </span>
                  </span>

                  <span class="owner-host-choice-action">
                    CHOOSE
                  </span>
                </button>
              `,
            )
            .join("")}
        </section>
      </main>
    `;

    return await new Promise<CanonicalHostId>(
      (resolve) => {
        const buttons =
          app.querySelectorAll<HTMLButtonElement>(
            "[data-host-id]",
          );

        for (const button of buttons) {
          button.addEventListener(
            "click",
            () => {
              const selectedHost =
                resolveCanonicalHost(
                  button.dataset.hostId ?? "",
                );

              if (!selectedHost) {
                return;
              }

              resolve(selectedHost.id);
            },
            {
              once: true,
            },
          );
        }
      },
    );
  }

  function mountOwnerShell():
  HTMLElement {
    app.innerHTML = `
      <section class="shell core-screen owner-shell">
        <header class="core-header">
          <p class="eyebrow">
            CUE THE NIGHT
          </p>

          <h1>
            Host<br />the night.
          </h1>

          <p class="subtitle">
            Getting your lobby ready…
          </p>
        </header>

        <section
          class="card core-surface"
          id="owner-panel"
        >
          <p class="status">
            Connecting to CUE THE NIGHT…
          </p>
        </section>
      </section>
    `;

    const panel =
      app.querySelector(
        "#owner-panel",
      );

    if (
      !(panel instanceof HTMLElement)
    ) {
      throw new Error(
        "Owner panel is missing.",
      );
    }

    return panel;
  }

  let panelElement:
    HTMLElement =
      mountOwnerShell();

  async function render(
    nextSnapshot:
      HostedPartySnapshot,
  ): Promise<void> {
    snapshot =
      nextSnapshot;

    if (
      gameSnapshot
    ) {
      renderOwnerGame({
        panel:
          panelElement,

        party:
          nextSnapshot,

        game:
          gameSnapshot,
      });

      const activeGame =
        gameSnapshot;

      const endGameButton =
        document.createElement(
          "button",
        );

      endGameButton.type =
        "button";

      endGameButton.className =
        "owner-end-game";

      endGameButton.textContent =
        "End Game";

      panelElement.append(
        endGameButton,
      );

      endGameButton
        .addEventListener(
          "click",
          async () => {
            if (!client) {
              return;
            }

            endGameButton.disabled =
              true;

            endGameButton.textContent =
              "Ending Game…";

            try {
              const ended =
                await client
                  .endGameSession(
                    activeGame
                      .gameSessionId,
                  );

              gameSnapshot =
                undefined;

              selectedGame =
                undefined;

              await render(
                ended.snapshot,
              );
            } catch (error) {
              endGameButton.disabled =
                false;

              endGameButton.textContent =
                error instanceof
                  HostedPartyClientError
                  ? error.message
                  : "Try End Game Again";
            }
          },
        );

      return;
    }

    const players =
      nextSnapshot.participants;

    const activeCount =
      players.filter(
        (player) =>
          player.status ===
            "ACTIVE",
      ).length;

    const roster =
      players.length === 0
        ? `
          <li class="owner-empty">
            Waiting for players…
          </li>
        `
        : players
            .map(
              (player) => `
                <li class="roster-player">
                  <strong>
                    ${escapeHtml(player.displayName)}
                  </strong>

                  <span
                    class="presence ${
                      player.status ===
                        "ACTIVE"
                        ? "active"
                        : "disconnected"
                    }"
                  >
                    ${
                      player.status ===
                        "ACTIVE"
                        ? "Connected"
                        : "Reconnecting"
                    }
                  </span>
                </li>
              `,
            )
            .join("");

    const url =
      joinUrl(
        nextSnapshot.joinCode,
      );

    const qr =
      await QRCode.toDataURL(
        url,
        {
          margin: 1,
          width: 320,
        },
      );

    const isActive =
      nextSnapshot.status ===
        "ACTIVE";

    const canStartNight =
      !isActive &&
      activeCount >= 2;

    /**
     * Canonical Truth or Dare rule:
     *
     * With exactly two players the Owner must explicitly
     * choose Party or Couples. We therefore keep Start Game
     * disabled until that choice has been made.
     */
    const canStartGame =
      isActive &&
      activeCount >= 2 &&
      activeCount !== 2;

    const categoryTabs =
      OWNER_GAME_CATEGORIES
        .map(
          (category) => `
            <button
              class="owner-category-tab ${
                selectedCategory ===
                  category.id
                  ? "is-active"
                  : ""
              }"
              type="button"
              data-owner-category="${category.id}"
              aria-pressed="${
                selectedCategory ===
                  category.id
                  ? "true"
                  : "false"
              }"
            >
              <strong>
                ${escapeHtml(category.shortTitle)}
              </strong>
            </button>
          `,
        )
        .join("");

    const selectedCategoryModel =
      OWNER_GAME_CATEGORIES.find(
        (category) =>
          category.id ===
          selectedCategory,
      );

    const categoryGames =
      ownerGamesForCategory(
        selectedCategory,
      );

    const catalogueCards =
      categoryGames.length ===
        0
        ? `
          <div class="owner-catalogue-empty">
            <span class="owner-game-monogram">
              ♥
            </span>

            <div>
              <p class="eyebrow">
                COUPLES GAMES
              </p>

              <h3>
                Made for two.
              </h3>

              <p>
                The first dedicated Couples games are coming next.
              </p>
            </div>
          </div>
        `
        : categoryGames
            .map(
              (game) => {
                const available =
                  game.availability ===
                  "AVAILABLE";

                return `
                  <button
                    class="owner-game-card ${
                      available
                        ? "is-available"
                        : "is-coming-soon"
                    }"
                    type="button"
                    ${
                      available
                        ? `data-owner-game="${game.id}"`
                        : "disabled"
                    }
                  >
                    <span class="owner-game-art">
                      ${
                        game.artwork
                          ? `
                            <img
                              src="${game.artwork}"
                              alt=""
                            />
                          `
                          : `
                            <span class="owner-game-monogram">
                              ${escapeHtml(game.monogram)}
                            </span>
                          `
                      }
                    </span>

                    <span class="owner-game-copy">
                      <span class="owner-game-kicker">
                        ${escapeHtml(game.eyebrow)}
                      </span>

                      <strong class="owner-game-title">
                        ${escapeHtml(game.title)}
                      </strong>

                      <span class="owner-game-description">
                        ${escapeHtml(game.description)}
                      </span>

                      <span class="owner-game-meta">
                        ${escapeHtml(game.meta)}
                      </span>
                    </span>

                    <span class="owner-game-status">
                      ${
                        available
                          ? "PLAY"
                          : "COMING SOON"
                      }
                    </span>
                  </button>
                `;
              },
            )
            .join("");

    panelElement.innerHTML = `
      <div class="owner-lobby">
        <div>
          <p class="eyebrow">
            ${
              isActive
                ? "NIGHT ACTIVE"
                : "LOBBY OPEN"
            }
          </p>

          <div class="owner-host">
            <span class="meta">
              HOST
            </span>

            <strong>
              ${escapeHtml(resolveCanonicalHost(nextSnapshot.activeVirtualHostId)?.displayName ?? nextSnapshot.activeVirtualHostId)}
            </strong>
          </div>
        </div>

        <div class="owner-join-grid">
          <div class="owner-code-block">
            <span class="meta">
              JOIN THE NIGHT
            </span>

            <div class="join-code">
              ${escapeHtml(nextSnapshot.joinCode)}
            </div>

            <div class="meta">
              ${activeCount}
              connected
            </div>
          </div>

          <div class="owner-qr">
            <img
              src="${qr}"
              alt="QR code to join CUE THE NIGHT"
            />
          </div>
        </div>

        ${
          stagePairCode
            ? `
              <div class="owner-code-block">
                <span class="meta">
                  STAGE PAIR
                </span>

                <div class="join-code">
                  ${escapeHtml(stagePairCode)}
                </div>

                <div class="meta">
                  Open /stage on the TV and enter this code.
                </div>
              </div>
            `
            : ""
        }

        <div class="roster-block">
          <div class="roster-heading">
            Players
          </div>

          <ul class="roster">
            ${roster}
          </ul>
        </div>

        ${
          isActive
            ? `
              <section
                class="owner-product-shell"
                ${
                  selectedGame
                    ? "hidden"
                    : ""
                }
              >
                ${
                  selectedCategoryModel
                    ? `
                      <div class="owner-category-visual">
                        <img
                          src="${selectedCategoryModel.artwork}"
                          alt=""
                        />

                        <div class="owner-category-visual-shade"></div>

                        <span>
                          ${escapeHtml(selectedCategoryModel.title)}
                        </span>
                      </div>
                    `
                    : ""
                }

                <div class="owner-product-heading">
                  <div>
                    <p class="eyebrow">
                      CHOOSE THE NIGHT
                    </p>

                    <h2>
                      What are we playing?
                    </h2>

                    <p>
                      ${
                        escapeHtml(
                          selectedCategoryModel
                            ?.description ??
                          "",
                        )
                      }
                    </p>
                  </div>

                  <span class="setup-badge">
                    ${activeCount} PLAYERS
                  </span>
                </div>

                <nav
                  class="owner-category-tabs"
                  aria-label="Game categories"
                >
                  ${categoryTabs}
                </nav>

                <div
                  class="owner-game-catalogue"
                  data-owner-catalogue="${selectedCategory}"
                >
                  ${catalogueCards}
                </div>
              </section>

              <section
                class="game-setup-panel owner-game-setup"
                aria-labelledby="tod-setup-title"
                ${
                  selectedGame ===
                    "truth-or-dare"
                    ? ""
                    : "hidden"
                }
              >
                <button
                  class="owner-game-back"
                  data-owner-back-to-games
                  type="button"
                >
                  ← All games
                </button>
                <div class="game-setup-heading">
                  <div>
                    <p class="eyebrow">
                      GAME SETUP
                    </p>

                    <h2 id="tod-setup-title">
                      Truth or Dare
                    </h2>
                  </div>

                  <span class="setup-badge">
                    ${activeCount} PLAYERS
                  </span>
                </div>

                <div class="game-setup-grid">
                  <label class="setup-field">
                    <span class="setup-label">
                      MODE
                    </span>

                    <select id="tod-mode">
                      ${
                        activeCount === 2
                          ? `
                            <option
                              value=""
                              selected
                            >
                              Choose Party or Couples…
                            </option>

                            <option value="party">
                              Party
                            </option>

                            <option value="couples">
                              Couples
                            </option>
                          `
                          : `
                            <option
                              value="party"
                              selected
                            >
                              Party
                            </option>
                          `
                      }
                    </select>

                    <span class="setup-help">
                      ${
                        activeCount === 2
                          ? "Two players can explicitly choose Party or Couples."
                          : "Couples is only available with exactly two players."
                      }
                    </span>
                  </label>

                  <label class="setup-field">
                    <span class="setup-label">
                      SPICE
                    </span>

                    <select id="tod-spice">
                      <option value="1">
                        1 · Easy
                      </option>

                      <option
                        value="2"
                        selected
                      >
                        2 · Playful
                      </option>

                      <option value="3">
                        3 · Bold
                      </option>

                      <option value="4">
                        4 · Wild
                      </option>

                      <option value="5">
                        5 · No Mercy
                      </option>
                    </select>

                    <span class="setup-help">
                      Spice is a ceiling. Lower levels can still appear.
                    </span>
                  </label>

                  <label class="setup-field">
                    <span class="setup-label">
                      LANGUAGE
                    </span>

                    <select id="tod-locale">
                      <option
                        value="en"
                        selected
                      >
                        English
                      </option>

                      <option value="nl">
                        Nederlands
                      </option>
                    </select>

                    <span class="setup-help">
                      Sets the language of canonical game prompts.
                    </span>
                  </label>

                  <label class="setup-toggle">
                    <span>
                      <strong>
                        Use card timers
                      </strong>

                      <span class="setup-help">
                        Only authored timed cards will use a timer.
                      </span>
                    </span>

                    <input
                      id="tod-timer"
                      type="checkbox"
                    />
                  </label>
                </div>

                <div class="setup-locked">
                  <strong>
                    Advanced adult settings
                  </strong>

                  <span>
                    After Dark, Nudity and Group Intimate remain locked
                    until the canonical per-player consent foundation is
                    implemented.
                  </span>
                </div>
              </section>
            `
            : ""
        }

        ${
          isActive
            ? `
              <section
                class="game-setup-panel hol-setup-panel owner-game-setup"
                aria-labelledby="hol-setup-title"
                ${
                  selectedGame ===
                    "high-or-low"
                    ? ""
                    : "hidden"
                }
              >
                <button
                  class="owner-game-back"
                  data-owner-back-to-games
                  type="button"
                >
                  ← All games
                </button>
                <div class="game-setup-heading">
                  <div>
                    <p class="eyebrow">
                      DRINKING GAME
                    </p>

                    <h2 id="hol-setup-title">
                      High or Low
                    </h2>
                  </div>

                  <span class="setup-badge">
                    6 PHASES
                  </span>
                </div>

                <div class="game-setup-grid">
                  <label class="setup-field">
                    <span class="setup-label">
                      DRINK PACE
                    </span>

                    <select id="hol-drink-pace">
                      <option value="light">
                        Light
                      </option>

                      <option
                        value="normal"
                        selected
                      >
                        Normal
                      </option>

                      <option value="heavy">
                        Heavy
                      </option>
                    </select>
                  </label>

                  <label class="setup-field">
                    <span class="setup-label">
                      LANGUAGE
                    </span>

                    <select id="hol-locale">
                      <option
                        value="en"
                        selected
                      >
                        English
                      </option>

                      <option value="nl">
                        Nederlands
                      </option>
                    </select>
                  </label>
                </div>

                <div class="setup-locked hol-setup-summary">
                  <strong>
                    Full six-phase game
                  </strong>

                  <span>
                    Higher or Lower · Red or Black · Suit ·
                    Lucky Streak · Double Down · Final Bus
                  </span>
                </div>

                <button
                  id="owner-high-or-low-action"
                  class="owner-secondary-game-action"
                  type="button"
                  ${
                    activeCount < 2
                      ? "disabled"
                      : ""
                  }
                >
                  Start High or Low
                </button>
              </section>
            `
            : ""
        }
        <button
          id="owner-action"
          type="button"
          ${
            (
              isActive &&
              selectedGame !==
                "truth-or-dare"
            )
              ? "hidden"
              : ""
          }
          ${
            (
              isActive
                ? !canStartGame
                : !canStartNight
            )
              ? "disabled"
              : ""
          }
        >
          ${
            isActive
              ? "Start Truth or Dare"
              : "Start Night"
          }
        </button>

        <p
          class="status"
          id="owner-status"
        >
          ${
            isActive
              ? selectedGame ===
                  "truth-or-dare"
                ? activeCount < 2
                  ? "Truth or Dare needs two active players."
                  : "Ready to start Truth or Dare."
                : selectedGame ===
                    "high-or-low"
                  ? activeCount < 2
                    ? "High or Low needs two active players."
                    : "Ready to start High or Low."
                  : "Choose a game to continue."
              : activeCount < 2
                ? "Waiting for at least two players."
                : "Ready when you are."
          }
        </p>
      </div>
    `;

    const actionButton =
      document.querySelector(
        "#owner-action",
      );

    const status =
      document.querySelector(
        "#owner-status",
      );

    const modeSelect =
      document.querySelector(
        "#tod-mode",
      );

    const spiceSelect =
      document.querySelector(
        "#tod-spice",
      );

    const localeSelect =
      document.querySelector(
        "#tod-locale",
      );

    const timerInput =
      document.querySelector(
        "#tod-timer",
      );

    const highLowButton =
      document.querySelector(
        "#owner-high-or-low-action",
      );

    const highLowPace =
      document.querySelector(
        "#hol-drink-pace",
      );

    const highLowLocale =
      document.querySelector(
        "#hol-locale",
      );

    const categoryButtons =
      panelElement
        .querySelectorAll<
          HTMLButtonElement
        >(
          "[data-owner-category]",
        );

    const gameButtons =
      panelElement
        .querySelectorAll<
          HTMLButtonElement
        >(
          "[data-owner-game]",
        );

    const backButtons =
      panelElement
        .querySelectorAll<
          HTMLButtonElement
        >(
          "[data-owner-back-to-games]",
        );

    for (
      const categoryButton of
      categoryButtons
    ) {
      categoryButton
        .addEventListener(
          "click",
          () => {
            const categoryId =
              categoryButton.dataset
                .ownerCategory;

            if (
              categoryId !==
                "party" &&
              categoryId !==
                "drinking" &&
              categoryId !==
                "couples"
            ) {
              return;
            }

            selectedCategory =
              categoryId;

            selectedGame =
              undefined;

            void render(
              nextSnapshot,
            );
          },
        );
    }

    for (
      const gameButton of
      gameButtons
    ) {
      gameButton
        .addEventListener(
          "click",
          () => {
            const gameId =
              gameButton.dataset
                .ownerGame;

            if (
              gameId !==
                "truth-or-dare" &&
              gameId !==
                "high-or-low"
            ) {
              return;
            }

            selectedGame =
              gameId;

            void render(
              nextSnapshot,
            );
          },
        );
    }

    for (
      const backButton of
      backButtons
    ) {
      backButton
        .addEventListener(
          "click",
          () => {
            selectedGame =
              undefined;

            void render(
              nextSnapshot,
            );
          },
        );
    }

    if (
      !(
        actionButton instanceof
          HTMLButtonElement
      ) ||
      !(
        status instanceof
          HTMLElement
      ) ||
      (
        isActive &&
        (
          !(
            modeSelect instanceof
              HTMLSelectElement
          ) ||
          !(
            spiceSelect instanceof
              HTMLSelectElement
          ) ||
          !(
            localeSelect instanceof
              HTMLSelectElement
          ) ||
          !(
            timerInput instanceof
              HTMLInputElement
          )
        )
      )
    ) {
      return;
    }

    if (
      isActive &&
      modeSelect instanceof
        HTMLSelectElement
    ) {
      const syncGameStartState =
        (): void => {
          actionButton.disabled =
            activeCount < 2 ||
            (
              activeCount === 2 &&
              modeSelect.value === ""
            );

          if (
            activeCount === 2 &&
            modeSelect.value === ""
          ) {
            status.textContent =
              "Choose Party or Couples to continue.";
          } else {
            status.textContent =
              "Ready to start Truth or Dare.";
          }
        };

      modeSelect.addEventListener(
        "change",
        syncGameStartState,
      );

      syncGameStartState();
    }

    if (
      isActive &&
      highLowButton instanceof
        HTMLButtonElement &&
      highLowPace instanceof
        HTMLSelectElement &&
      highLowLocale instanceof
        HTMLSelectElement
    ) {
      highLowButton.addEventListener(
        "click",
        async () => {
          if (!client) {
            return;
          }

          highLowButton.disabled =
            true;

          status.textContent =
            "Starting High or Low…";

          try {
            if (
              highLowPace.value !==
                "light" &&
              highLowPace.value !==
                "normal" &&
              highLowPace.value !==
                "heavy"
            ) {
              throw new Error(
                "Choose a valid drink pace.",
              );
            }

            if (
              highLowLocale.value !==
                "en" &&
              highLowLocale.value !==
                "nl"
            ) {
              throw new Error(
                "Choose a valid game language.",
              );
            }

            const created =
              await client
                .createGameSession(
                  "high-or-low",
                  {
                    drinkPace:
                      highLowPace.value,

                    penaltyMode:
                      "drink",

                    locale:
                      highLowLocale.value,
                  },
                );

            gameSnapshot =
              created.snapshot;

            await render(
              nextSnapshot,
            );
          } catch (error) {
            highLowButton.disabled =
              false;

            status.textContent =
              error instanceof
                HostedPartyClientError
                ? error.message
                : error instanceof Error
                  ? error.message
                  : "Unable to start High or Low.";
          }
        },
      );
    }
    actionButton.addEventListener(
      "click",
      async () => {
        if (!client) {
          return;
        }

        actionButton.disabled =
          true;

        status.textContent =
          isActive
            ? "Starting Truth or Dare…"
            : "Starting the night…";

        try {
          if (isActive) {
            if (
              !(
                modeSelect instanceof
                  HTMLSelectElement
              ) ||
              !(
                spiceSelect instanceof
                  HTMLSelectElement
              ) ||
              !(
                localeSelect instanceof
                  HTMLSelectElement
              ) ||
              !(
                timerInput instanceof
                  HTMLInputElement
              )
            ) {
              throw new Error(
                "Truth or Dare setup controls are unavailable.",
              );
            }

            if (
              modeSelect.value !==
                "party" &&
              modeSelect.value !==
                "couples"
            ) {
              status.textContent =
                "Choose Party or Couples to continue.";

              actionButton.disabled =
                true;

              return;
            }

            const spice =
              Number(
                spiceSelect.value,
              );

            if (
              !Number.isInteger(
                spice,
              ) ||
              spice < 1 ||
              spice > 5
            ) {
              throw new Error(
                "Choose a valid Spice level.",
              );
            }

            if (
              localeSelect.value !==
                "en" &&
              localeSelect.value !==
                "nl"
            ) {
              throw new Error(
                "Choose a valid game language.",
              );
            }

            const created =
              await client
                .createGameSession(
                  "truth-or-dare",
                  {
                    mode:
                      modeSelect.value,

                    spice,

                    timer:
                      timerInput.checked,

                    locale:
                      localeSelect.value,
                  },
                );

            gameSnapshot =
              created.snapshot;

            await render(
              nextSnapshot,
            );

            return;
          }

          const started =
            await client
              .startParty();

          await render(
            started.snapshot,
          );
        } catch (error) {
          actionButton.disabled =
            false;

          status.textContent =
            error instanceof
              HostedPartyClientError
              ? error.message
              : error instanceof Error
                ? error.message
                : isActive
                  ? "Unable to start Truth or Dare."
                  : "Unable to start the night.";
        }
      },
    );
  }

  let ownerLiveSessionReady =
    false;

  let ownerReconnectInFlight =
    false;

  let ownerClosing =
    false;

  function renderOwnerReconnect(
    message:
      string,
  ): void {
    gameSnapshot =
      undefined;

    panelElement.innerHTML = `
      <div class="owner-shell">
        <p class="eyebrow">
          OWNER CONNECTION INTERRUPTED
        </p>

        <h1>
          Reconnecting…
        </h1>

        <p class="status">
          ${escapeHtml(message)}
        </p>
      </div>
    `;
  }

  function waitForOwnerReconnect(
    delayMs:
      number,
  ): Promise<void> {
    return new Promise<void>(
      (resolve) => {
        window.setTimeout(
          () => resolve(),
          delayMs,
        );
      },
    );
  }

  async function recoverOwnerConnection():
  Promise<void> {
    if (
      ownerClosing ||
      ownerReconnectInFlight ||
      !ownerLiveSessionReady
    ) {
      return;
    }

    const saved =
      readSavedOwnerSession();

    if (!saved) {
      renderOwnerReconnect(
        "Owner recovery information is missing. Refresh to start a new lobby.",
      );

      ownerLiveSessionReady =
        false;

      return;
    }

    ownerReconnectInFlight =
      true;

    renderOwnerReconnect(
      "Restoring the authoritative party state.",
    );

    try {
      for (
        let attempt = 1;
        attempt <= 6;
        attempt++
      ) {
        try {
          const next =
            await connect();

          const resumed =
            await next
              .resumeParty(
                saved.reconnectToken,
              );

          if (
            resumed.role !==
              "SESSION_OWNER"
          ) {
            throw new Error(
              "Saved session is not an owner session.",
            );
          }

          stagePairCode =
            resumed
              .stagePairCode ??
            "";

          canonicalizeOwnerUrl(
            resumed.snapshot
              .joinCode,
          );

          ownerLiveSessionReady =
            true;

          await render(
            resumed.snapshot,
          );

          return;
        } catch (error) {
          if (
            error instanceof
              HostedPartyClientError &&
            error.code ===
              "INVALID_RECONNECT_TOKEN"
          ) {
            clearOwnerSession();

            client?.disconnect();

            client =
              undefined;

            ownerLiveSessionReady =
              false;

            renderOwnerReconnect(
              "This Owner session has expired. Refresh to create a new lobby.",
            );

            return;
          }

          client?.disconnect();

          client =
            undefined;

          if (
            attempt ===
              6
          ) {
            break;
          }

          renderOwnerReconnect(
            `Connection still unavailable. Retrying ${attempt + 1}/6…`,
          );

          await waitForOwnerReconnect(
            Math.min(
              5_000,
              attempt * 1_000,
            ),
          );
        }
      }

      ownerLiveSessionReady =
        false;

      renderOwnerReconnect(
        "The server is still unavailable. Refresh this Owner screen to retry.",
      );
    } finally {
      ownerReconnectInFlight =
        false;
    }
  }

  async function connect():
  Promise<HostedPartyClient> {
    unsubscribeParty?.();
    unsubscribeGame?.();

    client?.disconnect();

    const next =
      new HostedPartyClient(
        resolveWebSocketUrl(),
        () => {
          void recoverOwnerConnection();
        },
      );

    unsubscribeParty =
      next
        .subscribeToPartySnapshots(
          (message) => {
            if (
              !message.snapshot
                .currentGameSessionId
            ) {
              gameSnapshot =
                undefined;
            }

            void render(
              message.snapshot,
            );
          },
        );

    unsubscribeGame =
      next
        .subscribeToGameSnapshots(
          (message) => {
            gameSnapshot =
              message.snapshot;

            if (snapshot) {
              void render(
                snapshot,
              );
            }
          },
        );

    client =
      next;

    await next.connect();

    return next;
  }

  try {
    const next =
      await connect();

    const saved =
      readSavedOwnerSession();

    if (saved) {
      try {
        const resumed =
          await next
            .resumeParty(
              saved.reconnectToken,
            );

        if (
          resumed.role !==
            "SESSION_OWNER"
        ) {
          throw new Error(
            "Saved session is not an owner session.",
          );
        }

        stagePairCode =
          resumed
            .stagePairCode ??
          "";

        canonicalizeOwnerUrl(
          resumed.snapshot
            .joinCode,
        );

        ownerLiveSessionReady =
          true;

        await render(
          resumed.snapshot,
        );

        return;
      } catch (error) {
        if (
          error instanceof
            HostedPartyClientError &&
          error.code ===
            "INVALID_RECONNECT_TOKEN"
        ) {
          clearOwnerSession();

          next.disconnect();

          client =
            undefined;
        } else {
          throw error;
        }
      }
    }

    const fresh =
      client ??
      await connect();

    const selectedHost =
      await chooseHostForNewParty();

    /*
     * Host selection owns the full Owner root while choosing.
     * Remount the authoritative Owner shell before CREATE_PARTY
     * so snapshots and the initial render target a live DOM node.
     */
    panelElement =
      mountOwnerShell();

    const created =
      await fresh
        .createParty(
          selectedHost,
        );

    stagePairCode =
      created
        .stagePairCode;

    saveOwnerSession({
      joinCode:
        created.snapshot
          .joinCode,

      reconnectToken:
        created.reconnectToken,
    });

    installOwnerNewNightAction();

    canonicalizeOwnerUrl(
      created.snapshot
        .joinCode,
    );

    ownerLiveSessionReady =
      true;

    await render(
      created.snapshot,
    );
  } catch (error) {
    panelElement.innerHTML = `
      <p class="status">
        ${
          escapeHtml(
            error instanceof Error
              ? error.message
              : "Unable to create the lobby.",
          )
        }
      </p>
    `;
  }

  window.addEventListener(
    "beforeunload",
    () => {
      ownerClosing =
        true;

      unsubscribeParty?.();
      unsubscribeGame?.();

      client?.disconnect();
    },
  );
}
